import React, { useEffect, useMemo, useRef, useState } from "react";
import config from "../SupportFiles/config";
import useChartDimensions from "../SupportFiles/hooks/useChartDimensions";
import useScales from "../SupportFiles/hooks/useScales";
import { decimateLine } from "../SupportFiles/utils/decimate";
import Tooltip from "../SupportFiles/Tooltip";
import Loading from "../SupportFiles/Loading";
import Select from "../SupportFiles/Select";

const ChartBase = ({
  type,
  series = [],
  categories = [],
  options = {},
  height = "100%",
  width = "100%",
  className,
}) => {
  const { ref: containerRef, dims } = useChartDimensions({ w: 600, h: 300 });
  const padding = { ...config.padding, ...(options.padding || {}) };
  let innerW = Math.max(0, dims.w - padding.left - padding.right);
  const innerH = Math.max(0, dims.h - padding.top - padding.bottom);
  const legendMode = options?.legend?.mode || "inline";
  const axisX = options.axis?.x ?? options.xAxis ?? {};
  const axisY = options.axis?.y ?? options.yAxis ?? {};
  const isDiscrete = type === "column" || type === "bar";
  const [selectedIdx, setSelectedIdx] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  useEffect(() => {
    if (legendMode === "select") {
      const isGrouped =
        (options.bar?.grouped === true || options.chart?.grouped === true) &&
        (options.bar?.stacked === true || options.chart?.stacked === true);
      if (isGrouped) {
        const names = Array.from(new Set(series.map((s) => s.name)));
        setSelectedNames(names);
      } else {
        setSelectedIdx(series.map((_, idx) => idx));
      }
    }
  }, [legendMode, series, options]);
  const viewport = options.viewport || { start: 0, end: categories.length };
  const catWindow = categories.slice(viewport.start, viewport.end);
  const shouldDecimate =
    !isDiscrete &&
    (options.decimate?.enabled ?? config.decimate.enabled) &&
    innerW > 0;
  const maxPoints = Math.max(
    config.decimate.minPoints,
    Math.floor(
      innerW *
        (options.decimate?.targetPointsPerPixel ??
          config.decimate.targetPointsPerPixel)
    )
  );
  const effective = series.map((s, idx) => {
    const windowData = Array.isArray(s.data)
      ? s.data.slice(viewport.start, viewport.end)
      : [];
    const dec = shouldDecimate
      ? decimateLine(windowData, maxPoints)
      : windowData.map((y, i) => ({ i, y }));
    return { name: s.name, group: s.group, points: dec, origIdx: idx };
  });
  const displayEffective =
    legendMode === "select"
      ? options.bar?.grouped === true || options.chart?.grouped === true
        ? effective.filter((e) => selectedNames.includes(e.name))
        : selectedIdx.map((i) => effective[i]).filter(Boolean)
      : effective;
  const effectiveCategories =
    displayEffective.length > 0
      ? displayEffective[0].points.map((p) => catWindow[p.i])
      : catWindow;
  const stacked =
    options.chart?.stacked === true || options.bar?.stacked === true;
  const percentStack =
    stacked &&
    String(options.chart?.stackType ?? options.bar?.stackType).toLowerCase() ===
      "100%";
  const groupedMode =
    stacked &&
    (options.bar?.grouped === true || options.chart?.grouped === true);
  const totalsPerCategory = (() => {
    const len = displayEffective[0]?.points?.length || 0;
    return Array.from({ length: len }, (_, i) =>
      displayEffective.reduce((acc, e) => acc + (e.points[i]?.y || 0), 0)
    );
  })();
  const normalizedEffective = percentStack
    ? displayEffective.map((e) => ({
        name: e.name,
        origIdx: e.origIdx,
        points: e.points.map((p, i) => ({
          i,
          y: totalsPerCategory[i] ? (p.y / totalsPerCategory[i]) * 100 : 0,
        })),
      }))
    : displayEffective;
  // Build scale series for y-domain
  const scaleSeries = (() => {
    if (!stacked) return displayEffective;
    if (percentStack)
      return [
        {
          name: "pct-total",
          points: Array.from(
            { length: effectiveCategories.length },
            (_, i) => ({
              i,
              y: 100,
            })
          ),
        },
      ];
    if (groupedMode) {
      const groups = Array.from(
        new Set(displayEffective.map((e) => e.group ?? ""))
      );
      const maxPerCat = Array.from(
        { length: effectiveCategories.length },
        (_, i) => {
          let maxSum = 0;
          groups.forEach((g) => {
            const sum = displayEffective
              .filter((e) => (e.group ?? "") === g)
              .reduce((acc, e) => acc + (e.points[i]?.y || 0), 0);
            if (sum > maxSum) maxSum = sum;
          });
          return { i, y: maxSum };
        }
      );
      return [{ name: "group-max-total", points: maxPerCat }];
    }
    return [
      {
        name: "total",
        points: totalsPerCategory.map((y, i) => ({ i, y })),
      },
    ];
  })();
  const { minY, maxY, xCount, x, y, yTicks } = useScales({
    series: scaleSeries.map((e) => ({ data: e.points.map((p) => p.y) })),
    categories: effectiveCategories,
    padding,
    innerW,
    innerH,
    options,
  });
  const isLineLike = type === "line" || type === "area";
  const lineAreaOpts = type === "area" ? options.area : options.line;
  const strokeCfg = isLineLike
    ? lineAreaOpts?.stroke ?? options.stroke
    : options.stroke;
  const fillCfg = isLineLike
    ? lineAreaOpts?.fill ?? options.fill
    : options.fill;
  const markersCfg = isLineLike
    ? lineAreaOpts?.markers ?? options.markers
    : options.markers;
  const colors = options.colors || config.colors;
  const colorMap = options.seriesColorMap || {};
  const colorFor = (e) => colorMap[e.name] || colors[e.origIdx % colors.length];
  const perTypeOpts =
    type === "line"
      ? options.line || options
      : type === "area"
      ? options.area || options
      : type === "column" || type === "bar"
      ? options.bar || options
      : options;
  const rawLabelCfg =
    perTypeOpts?.showLabels ?? options.showLabels ?? config.showLabels;
  const labelCfg =
    typeof rawLabelCfg === "object" ? rawLabelCfg : { show: !!rawLabelCfg };
  const showLabels = labelCfg.show ?? true;
  const resolveColorToken = (token) => {
    if (!token) return undefined;
    if (typeof token !== "string") return token;
    if (token.startsWith("#")) return token;
    const parts = token.split("/");
    const base = parts[0];
    const segs = base.split("-");
    const name = segs[0];
    const depth = segs[1];
    const varName = depth ? `--color-${name}-${depth}` : `--color-${name}`;
    if (typeof window !== "undefined") {
      const cssVal = getComputedStyle(document.documentElement)
        .getPropertyValue(varName)
        .trim();
      if (cssVal) return cssVal;
    }
    const tf = {
      "gray-50": "#f9fafb",
      "gray-100": "#f3f4f6",
      "gray-200": "#e5e7eb",
      "gray-300": "#d1d5db",
      "gray-400": "#9ca3af",
      "gray-500": "#6b7280",
      "gray-600": "#4b5563",
      "gray-700": "#374151",
      "gray-800": "#1f2937",
      "gray-900": "#111827",
      "primary-500": "#3b82f6",
      "info-500": "#06b6d4",
      "danger-500": "#ef4444",
      "success-500": "#10b981",
      "slate-400": "#94a3b8",
    };
    const key = depth ? `${name}-${depth}` : name;
    return tf[key] || token;
  };
  const labelColor = resolveColorToken(labelCfg.color) ?? "#111827";
  const labelFontSize = labelCfg.fontSize ?? 10;
  const labelValueDisplay = labelCfg.valueDisplay ?? "value";
  const labelStrategy = labelCfg.strategy ?? "smart";
  const labelMinGap = labelCfg.minGap ?? 12;
  const labelMaxStack = Math.max(0, labelCfg.maxStack ?? 3);
  const labelStroke = labelCfg.stroke;
  const labelStrokeWidth = labelCfg.strokeWidth;
  const hexToRgba = (hex, alpha) => {
    const h = hex.replace("#", "");
    const bigint = parseInt(
      h.length === 3
        ? h
            .split("")
            .map((c) => c + c)
            .join("")
        : h,
      16
    );
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const buildPath = (data) => {
    if (!Array.isArray(data) || data.length === 0) return "";
    const strokeSrc = isLineLike
      ? lineAreaOpts?.stroke ?? options.stroke
      : options.stroke;
    const curve = strokeSrc?.curve ?? "straight";
    if (curve === "smooth") {
      const pts = data.map((v, i) => ({ x: x(i), y: y(v) }));
      if (pts.length < 2) return `M ${pts[0].x} ${pts[0].y}`;
      const t = Math.max(0, Math.min(1, strokeSrc?.tension ?? 0.4));
      let d = `M ${pts[0].x} ${pts[0].y}`;
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = i > 0 ? pts[i - 1] : pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = i + 2 < pts.length ? pts[i + 2] : p2;
        const c1x = p1.x + ((p2.x - p0.x) / 6) * t;
        const c1y = p1.y + ((p2.y - p0.y) / 6) * t;
        const c2x = p2.x - ((p3.x - p1.x) / 6) * t;
        const c2y = p2.y - ((p3.y - p1.y) / 6) * t;
        d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${p2.x} ${p2.y}`;
      }
      return d;
    } else if (curve === "stepline") {
      // Horizontal then vertical segments between points (step-after)
      let d = `M ${x(0)} ${y(data[0])}`;
      for (let i = 1; i < data.length; i++) {
        const prevY = y(data[i - 1]);
        const currY = y(data[i]);
        const xi = x(i);
        d += ` L ${xi} ${prevY} L ${xi} ${currY}`;
      }
      return d;
    }
    const pts = data.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(v)}`);
    return pts.join(" ");
  };

  const [tt, setTt] = useState({
    visible: false,
    x: 0,
    y: 0,
    title: "",
    subtitle: "",
  });
  const pathRefs = useRef([]);
  const [hidden, setHidden] = useState(new Set());
  const scrollYRef = useRef(null);
  const labelsYRef = useRef(null);
  const scrollbarComp = 14;

  useEffect(() => {
    if (!config.animations.enabled) return;
    pathRefs.current.forEach((ref) => {
      if (!ref) return;
      const len = ref.getTotalLength();
      ref.style.transition = "none";
      ref.style.strokeDasharray = `${len}`;
      ref.style.strokeDashoffset = `${len}`;
      ref.getBoundingClientRect();
      ref.style.transition = `stroke-dashoffset ${
        options.animations?.duration ?? config.animations.duration
      }ms ease`;
      ref.style.strokeDashoffset = "0";
    });
  }, [series, categories, dims.w, dims.h]);

  // isDiscrete defined above
  const scrollEnabled =
    isDiscrete && (axisX?.scroll?.enabled ?? options.scrollX?.enabled ?? false);
  const lineScrollEnabled =
    (type === "line" || type === "area") &&
    (axisX?.scroll?.enabled ?? options.scrollX?.enabled ?? false);
  const scrollBarEnabled =
    type === "bar" &&
    (axisY?.scroll?.enabled ?? options.scrollY?.enabled ?? false);
  const minBandWidth =
    axisX?.scroll?.minBandWidth ??
    axisX?.scroll?.minBarWidth ??
    options.scrollX?.minBandWidth ??
    options.scrollX?.minBarWidth ??
    24;
  const minBandHeight =
    axisY?.scroll?.minBandHeight ??
    axisY?.scroll?.minBarHeight ??
    options.scrollY?.minBandHeight ??
    options.scrollY?.minBarHeight ??
    24;
  const fixedBarW = options.bar?.fixedWidth;
  const fixedBarH = options.bar?.fixedHeight;
  const gapPx = options.bar?.gap ?? 6;
  const groupGapPx = options.bar?.groupGap ?? 0;
  const yLabelGutter = (() => {
    if (type !== "bar") return 0;
    if (typeof axisY?.labelGutter === "number") return axisY.labelGutter;
    const cats = effectiveCategories || [];
    const maxLen = cats.reduce(
      (m, c) => Math.max(m, String(c ?? "").length),
      0
    );
    const approx = Math.floor(maxLen * 6) + 16; // ~6px per char at fontSize 10 + padding
    return Math.min(260, Math.max(80, approx));
  })();
  const xLabelMinWidth = (() => {
    const override = axisX?.labelMinWidth;
    if (typeof override === "number" && override > 0) return override;
    const cats = effectiveCategories || [];
    const maxLen = cats.reduce(
      (m, c) => Math.max(m, String(c ?? "").length),
      0
    );
    const approx = Math.floor(maxLen * 6) + 16;
    return Math.min(300, Math.max(minBandWidth, approx));
  })();
  const desiredLineBandWidth = (() => {
    const bw = axisX?.scroll?.bandWidth ?? axisX?.scroll?.labelWidth;
    if (typeof bw === "number" && bw > 0) return bw;
    return Math.max(minBandWidth, xLabelMinWidth);
  })();
  const edgePadLine = (() => {
    const override =
      axisX?.scroll?.edgePadding ??
      axisX?.scroll?.labelPadding ??
      options.axis?.edgePadding ??
      config.axis?.edgePadding;
    if (typeof override === "number" && override >= 0) return override;
    return Math.max(10, Math.floor(xLabelMinWidth / 2));
  })();
  const seriesNForWidth = Math.max(
    1,
    Array.isArray(displayEffective) ? displayEffective.length : 1
  );
  const groupCountForWidth = groupedMode
    ? Math.max(1, new Set(displayEffective.map((e) => e.group ?? "")).size)
    : 1;
  const perCategoryWidth = (() => {
    if (stacked) {
      const groups = groupedMode ? groupCountForWidth : 1;
      if (typeof fixedBarW === "number" && fixedBarW > 0) {
        return Math.max(
          minBandWidth,
          gapPx * 2 + fixedBarW * groups + groupGapPx * Math.max(0, groups - 1)
        );
      }
      return Math.max(
        minBandWidth,
        gapPx * 2 + 12 * groups + groupGapPx * Math.max(0, groups - 1)
      );
    }
    if (typeof fixedBarW === "number" && fixedBarW > 0) {
      return Math.max(
        minBandWidth,
        groupGapPx + gapPx * (seriesNForWidth + 1) + fixedBarW * seriesNForWidth
      );
    }
    return Math.max(
      minBandWidth,
      groupGapPx + gapPx * (seriesNForWidth + 1) + 12
    );
  })();
  const seriesNForHeight = seriesNForWidth;
  const perCategoryHeight = (() => {
    if (stacked) {
      if (typeof fixedBarH === "number" && fixedBarH > 0) {
        return Math.max(minBandHeight, groupGapPx + gapPx * 2 + fixedBarH);
      }
      return Math.max(minBandHeight, groupGapPx + gapPx * 2 + 12);
    }
    if (typeof fixedBarH === "number" && fixedBarH > 0) {
      return Math.max(
        minBandHeight,
        groupGapPx +
          gapPx * (seriesNForHeight + 1) +
          fixedBarH * seriesNForHeight
      );
    }
    return Math.max(
      minBandHeight,
      groupGapPx + gapPx * (seriesNForHeight + 1) + 12
    );
  })();
  const contentInnerW = scrollEnabled
    ? Math.max(innerW, (catWindow?.length || 0) * perCategoryWidth)
    : innerW;
  const contentInnerWLine = lineScrollEnabled
    ? Math.max(
        innerW,
        Math.max(0, xCount - 1) * desiredLineBandWidth + edgePadLine * 2
      )
    : innerW;
  const contentInnerH = scrollBarEnabled
    ? Math.max(innerH, Math.floor((catWindow?.length || 0) * perCategoryHeight))
    : innerH;
  const [yScrollScale, setYScrollScale] = useState(null);
  const [xScrollInfo, setXScrollInfo] = useState({ start: 0, count: xCount });
  const scrollXRef = useRef(null);
  const yTicksToDraw =
    type === "column" &&
    scrollEnabled &&
    axisY?.rescaleOnScroll &&
    yScrollScale?.yTicks
      ? yScrollScale.yTicks
      : yTicks;
  const svgW = dims.w;

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ width, height, position: "relative" }}
    >
      {series.length > 0 ? (
        <svg width={svgW} height={dims.h}>
          <rect
            x="0"
            y="0"
            width={svgW}
            height={dims.h}
            fill={options.background || config.background}
          />
          <rect
            x={padding.left}
            y={padding.top}
            width={innerW}
            height={innerH}
            fill={options.grid?.background ?? config.grid.background}
          />
          {(options.grid?.horizontal ?? config.grid.horizontal) &&
            yTicksToDraw.length > 0 &&
            yTicksToDraw.map((t, i) => (
              <line
                key={`grid-h-${i}`}
                x1={padding.left}
                y1={t.y}
                x2={svgW - padding.right}
                y2={t.y}
                stroke={options.grid?.color || config.grid.color}
                strokeWidth={
                  options.grid?.strokeWidth ?? config.grid.strokeWidth
                }
                strokeDasharray={
                  options.grid?.dasharray ?? config.grid.dasharray
                }
              />
            ))}
          {!scrollEnabled &&
            !((type === "line" || type === "area") && lineScrollEnabled) &&
            (options.grid?.vertical ?? config.grid.vertical) &&
            xCount > 0 &&
            Array.from({ length: xCount }).map((_, i) => (
              <line
                key={`grid-v-${i}`}
                x1={x(i)}
                y1={padding.top}
                x2={x(i)}
                y2={padding.top + innerH}
                stroke={options.grid?.color || config.grid.color}
                strokeWidth={
                  options.grid?.strokeWidth ?? config.grid.strokeWidth
                }
                strokeDasharray={
                  options.grid?.dasharray ?? config.grid.dasharray
                }
              />
            ))}
          <line
            x1={padding.left}
            y1={padding.top + innerH}
            x2={svgW - padding.right}
            y2={padding.top + innerH}
            stroke={options.axis?.color || config.axis.color}
          />
          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={padding.top + innerH}
            stroke={options.axis?.color || config.axis.color}
          />
          {(() => {
            if (isDiscrete && scrollEnabled) return null;
            if ((type === "line" || type === "area") && lineScrollEnabled)
              return null;
            if (type === "bar") return null;
            const charPx = 6;
            const labelOverflowMode =
              axisX?.labelOverflow ??
              options.axis?.labelOverflow ??
              config.axis?.labelOverflow ??
              "step";
            const bandLine = xCount > 1 ? innerW / (xCount - 1) : innerW;
            if (labelOverflowMode === "step") {
              const approxLabels = Math.max(
                2,
                Math.floor(innerW / xLabelMinWidth)
              );
              const labelStepOverride = axisX.labelStep;
              const step =
                typeof labelStepOverride === "number" && labelStepOverride > 0
                  ? labelStepOverride
                  : Math.max(1, Math.ceil(xCount / approxLabels));
              return Array.from({ length: xCount }).map((_, i) => {
                if (i % step !== 0 && i !== xCount - 1) return null;
                const band = isDiscrete ? innerW / (xCount || 1) : bandLine;
                const lx = isDiscrete
                  ? padding.left + i * band + band / 2
                  : x(i);
                return (
                  <text
                    key={`xlab-${i}`}
                    x={lx}
                    y={padding.top + innerH + 16}
                    fontSize={10}
                    fill={options.axis?.labelColor || config.axis.labelColor}
                    textAnchor="middle"
                  >
                    {effectiveCategories?.[i] ?? i + 1}
                  </text>
                );
              });
            }
            if (labelOverflowMode === "rotate") {
              return Array.from({ length: xCount }).map((_, i) => {
                const band = isDiscrete ? innerW / (xCount || 1) : bandLine;
                const lx = isDiscrete
                  ? padding.left + i * band + band / 2
                  : x(i);
                const raw = effectiveCategories?.[i] ?? i + 1;
                const str = typeof raw === "string" ? raw : String(raw);
                const rot = band < 16 ? -90 : -45;
                return (
                  <text
                    key={`xlab-${i}`}
                    x={lx}
                    y={padding.top + innerH + 16}
                    fontSize={10}
                    fill={options.axis?.labelColor || config.axis.labelColor}
                    textAnchor={rot === 0 ? "middle" : "end"}
                    transform={`rotate(${rot} ${lx} ${
                      padding.top + innerH + 16
                    })`}
                  >
                    {str}
                  </text>
                );
              });
            }
            if (labelOverflowMode === "truncate") {
              return Array.from({ length: xCount }).map((_, i) => {
                const band = isDiscrete ? innerW / (xCount || 1) : bandLine;
                const lx = isDiscrete
                  ? padding.left + i * band + band / 2
                  : x(i);
                const raw = effectiveCategories?.[i] ?? i + 1;
                const str = typeof raw === "string" ? raw : String(raw);
                const maxChars = Math.max(1, Math.floor(band / charPx) - 1);
                const show =
                  str.length > maxChars
                    ? str.slice(0, Math.max(1, maxChars)) + "…"
                    : str;
                return (
                  <text
                    key={`xlab-${i}`}
                    x={lx}
                    y={padding.top + innerH + 16}
                    fontSize={10}
                    fill={options.axis?.labelColor || config.axis.labelColor}
                    textAnchor="middle"
                  >
                    {show}
                  </text>
                );
              });
            }
            return Array.from({ length: xCount }).map((_, i) => {
              const band = isDiscrete ? innerW / (xCount || 1) : bandLine;
              const lx = isDiscrete ? padding.left + i * band + band / 2 : x(i);
              return (
                <text
                  key={`xlab-${i}`}
                  x={lx}
                  y={padding.top + innerH + 16}
                  fontSize={10}
                  fill={options.axis?.labelColor || config.axis.labelColor}
                  textAnchor="middle"
                >
                  {effectiveCategories?.[i] ?? i + 1}
                </text>
              );
            });
          })()}

          {type !== "bar" &&
            yTicksToDraw.map((t, i) => (
              <text
                key={`ylab-${i}`}
                x={padding.left - 6}
                y={t.y + 3}
                fontSize={10}
                fill={options.axis?.labelColor || config.axis.labelColor}
                textAnchor="end"
              >
                {percentStack
                  ? `${Math.round(t.value)}%`
                  : axisY?.formatTick
                  ? axisY.formatTick(t.value)
                  : t.value}
              </text>
            ))}
          {(() => {
            if (type !== "bar" || scrollBarEnabled) return null;
            const bandY = innerH / (xCount || 1);
            return Array.from({ length: xCount }).map((_, i) => (
              <text
                key={`ylab-cat-${i}`}
                x={padding.left - 6}
                y={padding.top + i * bandY + bandY / 2 + 3}
                fontSize={10}
                fill={options.axis?.labelColor || config.axis.labelColor}
                textAnchor="end"
              >
                {effectiveCategories?.[i] ?? i + 1}
              </text>
            ));
          })()}
          {(() => {
            if (type !== "bar") return null;
            const valXFor = (v) =>
              padding.left + ((v - minY) / (maxY - minY || 1)) * innerW;
            return yTicksToDraw.map((t, i) => (
              <text
                key={`xlab-num-${i}`}
                x={valXFor(t.value)}
                y={padding.top + innerH + 16}
                fontSize={10}
                fill={options.axis?.labelColor || config.axis.labelColor}
                textAnchor="middle"
              >
                {percentStack
                  ? `${Math.round(t.value)}%`
                  : axisX?.formatTick
                  ? axisX.formatTick(t.value)
                  : t.value}
              </text>
            ));
          })()}
          {(() => {
            if (type === "line" || type === "area") {
              if (lineScrollEnabled) return null;
              const lineLabelSlots = Array.from({ length: xCount }, () => []);
              return displayEffective.map((e, si) => {
                const color = colorFor(e);
                if (hidden.has(si)) return null;
                return (
                  <g key={`s-${si}`}>
                    <path
                      ref={(el) => (pathRefs.current[si] = el)}
                      d={buildPath(e.points.map((p) => p.y))}
                      fill="none"
                      stroke={color}
                      strokeWidth={strokeCfg?.width ?? 2}
                      strokeDasharray={
                        strokeCfg?.dashArray ??
                        strokeCfg?.dasharray ??
                        undefined
                      }
                    />
                    {(() => {
                      const fillType = fillCfg?.type ?? "none";
                      if (!fillType || fillType === "none") return null;
                      const data = e.points.map((p) => p.y);
                      if (!Array.isArray(data) || data.length === 0)
                        return null;
                      let d = buildPath(data);
                      const lastX = x(data.length - 1);
                      const baseY = padding.top + innerH;
                      d += ` L ${lastX} ${baseY} L ${x(0)} ${baseY} Z`;
                      if (fillType === "solid") {
                        const alpha = fillCfg?.opacity ?? 0.15;
                        return <path d={d} fill={hexToRgba(color, alpha)} />;
                      }
                      if (fillType === "gradient") {
                        const gid = `grad-${si}`;
                        const alpha = fillCfg?.opacity ?? 0.15;
                        return (
                          <g>
                            <defs>
                              <linearGradient
                                id={gid}
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="0%"
                                  stopColor={hexToRgba(color, alpha)}
                                />
                                <stop
                                  offset="100%"
                                  stopColor={hexToRgba(color, 0)}
                                />
                              </linearGradient>
                            </defs>
                            <path d={d} fill={`url(#${gid})`} />
                          </g>
                        );
                      }
                      return null;
                    })()}
                    {e.points.map((p, i) => (
                      <circle
                        key={`p-${si}-${i}`}
                        cx={x(i)}
                        cy={y(p.y)}
                        r={markersCfg?.size ?? 2.5}
                        fill={color}
                        onMouseEnter={() => {
                          const px = x(i);
                          const py = y(p.y);
                          const estWidth = 160;
                          let left = px + 10;
                          let top = py - 24;
                          if (left + estWidth > dims.w - padding.right)
                            left = px - estWidth - 10;
                          if (top < padding.top) top = py + 12;
                          setTt({
                            visible: true,
                            x: left,
                            y: top,
                            title: `${e.name ?? `Series ${si + 1}`}`,
                            subtitle: `${effectiveCategories?.[i] ?? i + 1}: ${
                              p.y
                            }`,
                          });
                        }}
                        onMouseMove={() => {
                          const px = x(i);
                          const py = y(p.y);
                          const estWidth = 160;
                          let left = px + 10;
                          let top = py - 24;
                          if (left + estWidth > dims.w - padding.right)
                            left = px - estWidth - 10;
                          if (top < padding.top) top = py + 12;
                          setTt((t) => ({ ...t, x: left, y: top }));
                        }}
                        onMouseLeave={() =>
                          setTt((t) => ({ ...t, visible: false }))
                        }
                      />
                    ))}
                    {showLabels &&
                      e.points.map((p, i) => {
                        const totalCat = totalsPerCategory[i] || 0;
                        const pct =
                          totalCat > 0 ? Math.round((p.y / totalCat) * 100) : 0;
                        const text =
                          labelValueDisplay === "percentage"
                            ? `${pct}%`
                            : labelValueDisplay === "value_percentage"
                            ? `${String(p.y)} / ${pct}%`
                            : String(p.y);
                        const baseY = y(p.y) - 8;
                        const slots = lineLabelSlots[i];
                        let pyFinal = baseY;
                        if (labelStrategy === "hide") {
                          for (let s of slots) {
                            if (Math.abs(s - baseY) < labelMinGap) return null;
                          }
                        } else {
                          const step = labelFontSize + 2;
                          const candidates = [0];
                          for (let k = 1; k <= labelMaxStack; k++) {
                            candidates.push(-k * step, k * step);
                          }
                          let placed = false;
                          for (let ci = 0; ci < candidates.length; ci++) {
                            const cand = baseY + candidates[ci];
                            let collides = false;
                            for (let s of slots) {
                              if (Math.abs(s - cand) < labelMinGap) {
                                collides = true;
                                break;
                              }
                            }
                            if (!collides) {
                              pyFinal = cand;
                              placed = true;
                              break;
                            }
                          }
                          if (!placed && labelStrategy === "hide") return null;
                        }
                        slots.push(pyFinal);
                        return (
                          <text
                            key={`pl-${si}-${i}`}
                            x={x(i)}
                            y={pyFinal}
                            fontSize={labelFontSize}
                            textAnchor="middle"
                            fill={labelColor}
                            stroke={labelStroke}
                            strokeWidth={labelStrokeWidth}
                          >
                            {text}
                          </text>
                        );
                      })}
                  </g>
                );
              });
            }
            if (
              (type === "column" && scrollEnabled) ||
              (type === "bar" && scrollBarEnabled) ||
              ((type === "line" || type === "area") && lineScrollEnabled)
            ) {
              return null;
            }
            if (type === "column" || type === "bar") {
              const isHorizontal = type === "bar";
              const bandX = innerW / (xCount || 1);
              const bandY = innerH / (xCount || 1);
              const gap = options.bar?.gap ?? 6;
              const radius = options.bar?.radius ?? 2;
              const valX = (v) =>
                padding.left + ((v - minY) / (maxY - minY || 1)) * innerW;
              const clampZeroY =
                (options.axis?.y ?? options.yAxis)?.clampMinZero ??
                config.axis?.y?.clampMinZero ??
                false;
              const baselineVal =
                clampZeroY || stacked
                  ? 0
                  : minY > 0
                  ? minY
                  : maxY < 0
                  ? maxY
                  : 0;
              if (!stacked) {
                const seriesN = Math.max(1, displayEffective.length);
                const fixedW = options.bar?.fixedWidth;
                const barW = Math.max(1, bandX - gap * (seriesN + 1));
                const perSeriesW =
                  typeof fixedW === "number" && fixedW > 0
                    ? fixedW
                    : Math.max(1, barW / seriesN);
                const perSeriesH = Math.max(
                  1,
                  Math.max(1, bandY - gap * (seriesN + 1)) / seriesN
                );
                const columnLabelSlots = Array.from(
                  { length: xCount },
                  () => []
                );
                return displayEffective.map((e, si) => {
                  const color = colorFor(e);
                  if (hidden.has(si)) return null;
                  return (
                    <g key={`s-${si}`}>
                      {e.points.map((p, i) => {
                        if (!isHorizontal) {
                          const groupWidth =
                            perSeriesW * seriesN +
                            gap * Math.max(0, seriesN - 1);
                          const xStart =
                            padding.left + i * bandX + (bandX - groupWidth) / 2;
                          const gx = xStart + si * (perSeriesW + gap);
                          const vy = y(p.y);
                          const baseY = y(baselineVal);
                          const ry = Math.min(vy, baseY);
                          const rh = Math.abs(baseY - vy);
                          const px = gx + perSeriesW / 2;
                          const py = ry;
                          return (
                            <g key={`c-${si}-${i}`}>
                              <rect
                                x={gx}
                                y={ry}
                                width={perSeriesW}
                                height={rh}
                                fill={color}
                                rx={radius}
                                onMouseEnter={() => {
                                  const estWidth = 160;
                                  const px2 = gx + perSeriesW / 2;
                                  const py2 = ry;
                                  let left = px2 + 10;
                                  let top = py2 - 24;
                                  if (left + estWidth > dims.w - padding.right)
                                    left = px2 - estWidth - 10;
                                  if (top < padding.top) top = py2 + 12;
                                  setTt({
                                    visible: true,
                                    x: left,
                                    y: top,
                                    title: `${e.name ?? `Series ${si + 1}`}`,
                                    subtitle: `${
                                      effectiveCategories?.[i] ?? i + 1
                                    }: ${p.y}`,
                                  });
                                }}
                                onMouseLeave={() =>
                                  setTt((t) => ({ ...t, visible: false }))
                                }
                              />
                              {showLabels && rh > 14 && perSeriesW >= 10
                                ? (() => {
                                    const totalCat = totalsPerCategory[i] || 0;
                                    const pct =
                                      totalCat > 0
                                        ? Math.round(
                                            ((p.y || 0) / totalCat) * 100
                                          )
                                        : 0;
                                    const text =
                                      labelValueDisplay === "percentage"
                                        ? `${pct}%`
                                        : labelValueDisplay ===
                                          "value_percentage"
                                        ? `${String(p.y)} / ${pct}%`
                                        : String(p.y);
                                    const baseY = ry - 4;
                                    const slots = columnLabelSlots[i];
                                    let offsetN = 0;
                                    for (let k = 0; k < slots.length; k++) {
                                      if (
                                        Math.abs(
                                          slots[k] -
                                            (baseY -
                                              offsetN * (labelFontSize + 2))
                                        ) < (labelCfg.minGap ?? 12)
                                      ) {
                                        offsetN++;
                                      }
                                    }
                                    const pyFinal =
                                      baseY - offsetN * (labelFontSize + 2);
                                    slots.push(pyFinal);
                                    return (
                                      <text
                                        x={px}
                                        y={pyFinal}
                                        fontSize={labelFontSize}
                                        textAnchor="middle"
                                        fill={labelColor}
                                      >
                                        {text}
                                      </text>
                                    );
                                  })()
                                : null}
                            </g>
                          );
                        } else {
                          const gy =
                            padding.top +
                            i * bandY +
                            gap +
                            si * (perSeriesH + gap);
                          const vx = valX(p.y);
                          const clampZeroY =
                            (options.axis?.y ?? options.yAxis)?.clampMinZero ??
                            config.axis?.y?.clampMinZero ??
                            false;
                          const baseX = valX(
                            clampZeroY || stacked ? 0 : baselineVal
                          );
                          const rx2 = Math.min(vx, baseX);
                          const rw = Math.abs(vx - baseX);
                          return (
                            <g key={`b-${si}-${i}`}>
                              <rect
                                x={rx2}
                                y={gy}
                                width={rw}
                                height={perSeriesH}
                                fill={color}
                                ry={radius}
                                onMouseEnter={() => {
                                  const estWidth = 160;
                                  const cx = rx2 + rw / 2;
                                  const cy = gy + perSeriesH / 2;
                                  let left = cx + 10;
                                  let top = cy - 24;
                                  if (left + estWidth > dims.w - padding.right)
                                    left = cx - estWidth - 10;
                                  if (top < padding.top) top = cy + 12;
                                  setTt({
                                    visible: true,
                                    x: left,
                                    y: top,
                                    title: `${e.name ?? `Series ${si + 1}`}`,
                                    subtitle: `${
                                      effectiveCategories?.[i] ?? i + 1
                                    }: ${p.y}`,
                                  });
                                }}
                                onMouseLeave={() =>
                                  setTt((t) => ({ ...t, visible: false }))
                                }
                              />
                              {showLabels && rw > 20 && perSeriesH >= 10
                                ? (() => {
                                    const totalCat = totalsPerCategory[i] || 0;
                                    const pct =
                                      totalCat > 0
                                        ? Math.round(
                                            ((p.y || 0) / totalCat) * 100
                                          )
                                        : 0;
                                    const text =
                                      labelValueDisplay === "percentage"
                                        ? `${pct}%`
                                        : labelValueDisplay ===
                                          "value_percentage"
                                        ? `${String(p.y)} / ${pct}%`
                                        : String(p.y);
                                    const baseY = gy + perSeriesH / 2 + 3;
                                    const slots = columnLabelSlots[i];
                                    let pyFinal = baseY;
                                    let placed = true;
                                    const step = labelFontSize + 2;
                                    const candidates = [0];
                                    for (let k = 1; k <= labelMaxStack; k++) {
                                      candidates.push(-k * step, k * step);
                                    }
                                    for (
                                      let ci = 0;
                                      ci < candidates.length;
                                      ci++
                                    ) {
                                      const cand = baseY + candidates[ci];
                                      let collides = false;
                                      for (let s of slots) {
                                        if (Math.abs(s - cand) < labelMinGap) {
                                          collides = true;
                                          break;
                                        }
                                      }
                                      if (!collides) {
                                        pyFinal = cand;
                                        break;
                                      }
                                      if (ci === candidates.length - 1) {
                                        placed = false;
                                      }
                                    }
                                    if (!placed && labelStrategy === "hide")
                                      return null;
                                    slots.push(pyFinal);
                                    return (
                                      <text
                                        x={rx2 + rw + 4}
                                        y={pyFinal}
                                        fontSize={labelFontSize}
                                        textAnchor="start"
                                        fill={labelColor}
                                        stroke={labelStroke}
                                        strokeWidth={labelStrokeWidth}
                                      >
                                        {text}
                                      </text>
                                    );
                                  })()
                                : null}
                            </g>
                          );
                        }
                      })}
                    </g>
                  );
                });
              }
              const groupedMode =
                stacked &&
                (options.bar?.grouped === true ||
                  options.chart?.grouped === true);
              const seriesToDraw = percentStack
                ? normalizedEffective
                : displayEffective;
              if (!isHorizontal) {
                const fixedW = options.bar?.fixedWidth;
                const barFullW =
                  typeof fixedW === "number" && fixedW > 0
                    ? Math.min(fixedW, Math.max(1, bandX))
                    : Math.max(1, bandX - gap * 2);
                if (!groupedMode) {
                  return (
                    <g key={`stack-col`}>
                      {Array.from({ length: xCount }).map((_, i) => {
                        const gx =
                          padding.left + i * bandX + (bandX - barFullW) / 2;
                        let acc = 0;
                        return seriesToDraw.map((e, si) => {
                          const val = e.points[i]?.y || 0;
                          const y1 = y(acc);
                          const nextAcc =
                            percentStack && si === seriesToDraw.length - 1
                              ? 100
                              : acc + val;
                          const y2 = y(nextAcc);
                          const ry = Math.min(y1, y2);
                          const rh = Math.abs(y1 - y2);
                          const c = colorFor(e);
                          acc = nextAcc;
                          const px = gx + barFullW / 2;
                          const py = ry + rh / 2;
                          const rawVal = percentStack
                            ? displayEffective[si]?.points[i]?.y ?? 0
                            : val;
                          return (
                            <g key={`sc-${si}-${i}`}>
                              <rect
                                x={gx}
                                y={ry}
                                width={barFullW}
                                height={rh}
                                fill={c}
                                rx={radius}
                                onMouseEnter={() => {
                                  const estWidth = 160;
                                  let left = px + 10;
                                  let top = py - 24;
                                  if (left + estWidth > dims.w - padding.right)
                                    left = px - estWidth - 10;
                                  if (top < padding.top) top = py + 12;
                                  const pctText = percentStack
                                    ? `${Math.round(val)}%`
                                    : `${rawVal}`;
                                  setTt({
                                    visible: true,
                                    x: left,
                                    y: top,
                                    title: `${
                                      e.name ?? `Series ${e.origIdx + 1}`
                                    }`,
                                    subtitle: `${
                                      effectiveCategories?.[i] ?? i + 1
                                    }: ${pctText}${
                                      percentStack ? ` (${rawVal})` : ""
                                    }`,
                                  });
                                }}
                                onMouseLeave={() =>
                                  setTt((t) => ({ ...t, visible: false }))
                                }
                              />
                              {percentStack && rh > 12 ? (
                                <text
                                  x={px}
                                  y={py + 3}
                                  fontSize={10}
                                  fill="#ffffff"
                                  textAnchor="middle"
                                >
                                  {Math.round(val)}%
                                </text>
                              ) : null}
                              {!percentStack && showLabels && rh > 12
                                ? (() => {
                                    const totalCat = totalsPerCategory[i] || 0;
                                    const pct =
                                      totalCat > 0
                                        ? Math.round(
                                            ((rawVal || 0) / totalCat) * 100
                                          )
                                        : 0;
                                    const text =
                                      labelValueDisplay === "percentage"
                                        ? `${pct}%`
                                        : labelValueDisplay ===
                                          "value_percentage"
                                        ? `${String(rawVal)} / ${pct}%`
                                        : String(rawVal);
                                    return (
                                      <text
                                        x={px}
                                        y={py + 3}
                                        fontSize={labelFontSize}
                                        fill={labelColor}
                                        textAnchor="middle"
                                        stroke={labelStroke}
                                        strokeWidth={labelStrokeWidth}
                                      >
                                        {text}
                                      </text>
                                    );
                                  })()
                                : null}
                            </g>
                          );
                        });
                      })}
                    </g>
                  );
                }
                const groupKeys = Array.from(
                  new Set(effective.map((e) => e.group ?? ""))
                );
                const groupCount = Math.max(1, groupKeys.length);
                const groupGap = options.bar?.groupGap ?? 0;
                const totalAvail = Math.max(
                  1,
                  barFullW - groupGap * Math.max(0, groupCount - 1)
                );
                const perGroupW = Math.max(
                  1,
                  Math.floor(totalAvail / groupCount)
                );
                return (
                  <g key={`gstack-col`}>
                    {Array.from({ length: xCount }).map((_, i) => {
                      const bandStart =
                        padding.left + i * bandX + (bandX - barFullW) / 2;
                      return groupKeys.map((gk, gi) => {
                        const gx = bandStart + gi * (perGroupW + groupGap);
                        let acc = 0;
                        const groupSeries = seriesToDraw.filter(
                          (e) => (e.group ?? "") === gk
                        );
                        const groupTotalRaw = groupSeries.reduce(
                          (sum, e) =>
                            sum + (effective[e.origIdx]?.points[i]?.y || 0),
                          0
                        );
                        return groupSeries.map((e, si) => {
                          const raw = effective[e.origIdx]?.points[i]?.y || 0;
                          const val = percentStack
                            ? groupTotalRaw
                              ? (raw / groupTotalRaw) * 100
                              : 0
                            : raw;
                          const y1 = y(acc);
                          const nextAcc = acc + val;
                          const y2 = y(nextAcc);
                          const ry = Math.min(y1, y2);
                          const rh = Math.abs(y1 - y2);
                          const c = colorFor(e);
                          acc = nextAcc;
                          const px = gx + perGroupW / 2;
                          const py = ry + rh / 2;
                          const pctText = percentStack
                            ? `${Math.round(val)}%`
                            : `${raw}`;
                          return (
                            <g key={`gsc-${gi}-${si}-${i}`}>
                              <rect
                                x={gx}
                                y={ry}
                                width={perGroupW}
                                height={rh}
                                fill={c}
                                rx={radius}
                                onMouseEnter={() => {
                                  const estWidth = 160;
                                  let left = px + 10;
                                  let top = py - 24;
                                  if (left + estWidth > dims.w - padding.right)
                                    left = px - estWidth - 10;
                                  if (top < padding.top) top = py + 12;
                                  const monthLabel =
                                    effectiveCategories?.[i] ?? i + 1;
                                  const infoList = groupSeries
                                    .map((ge) => {
                                      const rawVal =
                                        effective[ge.origIdx]?.points[i]?.y ||
                                        0;
                                      return `${ge.name}: ${rawVal}`;
                                    })
                                    .join("\n");
                                  setTt({
                                    visible: true,
                                    x: left,
                                    y: top,
                                    title: `${gk} — ${monthLabel}`,
                                    subtitle: infoList,
                                  });
                                }}
                                onMouseLeave={() =>
                                  setTt((t) => ({ ...t, visible: false }))
                                }
                              />
                              {percentStack && rh > 12 ? (
                                <text
                                  x={px}
                                  y={py + 3}
                                  fontSize={10}
                                  fill="#ffffff"
                                  textAnchor="middle"
                                >
                                  {Math.round(val)}%
                                </text>
                              ) : null}
                              {!percentStack && showLabels && rh > 12 ? (
                                <text
                                  x={px}
                                  y={py + 3}
                                  fontSize={labelFontSize}
                                  fill={labelColor}
                                  textAnchor="middle"
                                  stroke={labelStroke}
                                  strokeWidth={labelStrokeWidth}
                                >
                                  {String(raw)}
                                </text>
                              ) : null}
                            </g>
                          );
                        });
                      });
                    })}
                  </g>
                );
              } else {
                const fixedH = options.bar?.fixedHeight;
                const barFullH =
                  typeof fixedH === "number" && fixedH > 0
                    ? Math.min(fixedH, Math.max(1, bandY))
                    : Math.max(1, bandY - gap * 2);
                if (!groupedMode) {
                  return (
                    <g key={`stack-bar`}>
                      {Array.from({ length: xCount }).map((_, i) => {
                        const gy =
                          padding.top + i * bandY + (bandY - barFullH) / 2;
                        let acc = 0;
                        return seriesToDraw.map((e, si) => {
                          const val = e.points[i]?.y || 0;
                          const x1 = valX(acc);
                          const nextAcc =
                            percentStack && si === seriesToDraw.length - 1
                              ? 100
                              : acc + val;
                          const x2 = valX(nextAcc);
                          const rx2 = Math.min(x1, x2);
                          const rw = Math.abs(x2 - x1);
                          const c = colorFor(e);
                          acc = nextAcc;
                          const cx = rx2 + rw / 2;
                          const cy = gy + barFullH / 2;
                          const rawVal = percentStack
                            ? displayEffective[si]?.points[i]?.y ?? 0
                            : val;
                          const pctText = percentStack
                            ? `${Math.round(val)}%`
                            : `${rawVal}`;
                          return (
                            <g key={`sb-${si}-${i}`}>
                              <rect
                                x={rx2}
                                y={gy}
                                width={rw}
                                height={barFullH}
                                fill={c}
                                ry={radius}
                                onMouseEnter={() => {
                                  const estWidth = 160;
                                  let left = cx + 10;
                                  let top = cy - 24;
                                  if (left + estWidth > dims.w - padding.right)
                                    left = cx - estWidth - 10;
                                  if (top < padding.top) top = cy + 12;
                                  setTt({
                                    visible: true,
                                    x: left,
                                    y: top,
                                    title: `${
                                      e.name ?? `Series ${e.origIdx + 1}`
                                    }`,
                                    subtitle: `${
                                      effectiveCategories?.[i] ?? i + 1
                                    }: ${pctText}${
                                      percentStack ? ` (${rawVal})` : ""
                                    }`,
                                  });
                                }}
                                onMouseLeave={() =>
                                  setTt((t) => ({ ...t, visible: false }))
                                }
                              />
                              {percentStack && rw > 24 ? (
                                <text
                                  x={cx}
                                  y={cy + 3}
                                  fontSize={10}
                                  fill="#ffffff"
                                  textAnchor="middle"
                                >
                                  {Math.round(val)}%
                                </text>
                              ) : null}
                              {!percentStack && showLabels && rw > 24 ? (
                                <text
                                  x={cx}
                                  y={cy + 3}
                                  fontSize={labelFontSize}
                                  fill={labelColor}
                                  textAnchor="middle"
                                  stroke={labelStroke}
                                  strokeWidth={labelStrokeWidth}
                                >
                                  {String(rawVal)}
                                </text>
                              ) : null}
                            </g>
                          );
                        });
                      })}
                    </g>
                  );
                }
                const groupKeys = Array.from(
                  new Set(effective.map((e) => e.group ?? ""))
                );
                const groupCount = Math.max(1, groupKeys.length);
                const groupGap = options.bar?.groupGap ?? 0;
                const totalAvail = Math.max(
                  1,
                  barFullH - groupGap * Math.max(0, groupCount - 1)
                );
                const perGroupH = Math.max(
                  1,
                  Math.floor(totalAvail / groupCount)
                );
                return (
                  <g key={`gstack-bar`}>
                    {Array.from({ length: xCount }).map((_, i) => {
                      const bandStartY =
                        padding.top + i * bandY + (bandY - barFullH) / 2;
                      return groupKeys.map((gk, gi) => {
                        const gy = bandStartY + gi * (perGroupH + groupGap);
                        let acc = 0;
                        const groupSeries = seriesToDraw.filter(
                          (e) => (e.group ?? "") === gk
                        );
                        const groupTotalRaw = groupSeries.reduce(
                          (sum, e) =>
                            sum + (effective[e.origIdx]?.points[i]?.y || 0),
                          0
                        );
                        return groupSeries.map((e, si) => {
                          const raw = effective[e.origIdx]?.points[i]?.y || 0;
                          const val = percentStack
                            ? groupTotalRaw
                              ? (raw / groupTotalRaw) * 100
                              : 0
                            : raw;
                          const x1 = valX(acc);
                          const nextAcc = acc + val;
                          const x2 = valX(nextAcc);
                          const rx2 = Math.min(x1, x2);
                          const rw = Math.abs(x2 - x1);
                          const c = colorFor(e);
                          acc = nextAcc;
                          const cx = rx2 + rw / 2;
                          const cy = gy + perGroupH / 2;
                          const pctText = percentStack
                            ? `${Math.round(val)}%`
                            : `${raw}`;
                          return (
                            <g key={`gsb-${gi}-${si}-${i}`}>
                              <rect
                                x={rx2}
                                y={gy}
                                width={rw}
                                height={perGroupH}
                                fill={c}
                                ry={radius}
                                onMouseEnter={() => {
                                  const estWidth = 160;
                                  let left = cx + 10;
                                  let top = cy - 24;
                                  if (left + estWidth > dims.w - padding.right)
                                    left = cx - estWidth - 10;
                                  if (top < padding.top) top = cy + 12;
                                  const monthLabel =
                                    effectiveCategories?.[i] ?? i + 1;
                                  const infoList = groupSeries
                                    .map((ge) => {
                                      const rawVal =
                                        effective[ge.origIdx]?.points[i]?.y ||
                                        0;
                                      return `${ge.name}: ${rawVal}`;
                                    })
                                    .join("\n");
                                  setTt({
                                    visible: true,
                                    x: left,
                                    y: top,
                                    title: `${gk} — ${monthLabel}`,
                                    subtitle: infoList,
                                  });
                                }}
                                onMouseLeave={() =>
                                  setTt((t) => ({ ...t, visible: false }))
                                }
                              />
                              {percentStack && rw > 24 ? (
                                <text
                                  x={cx}
                                  y={cy + 3}
                                  fontSize={10}
                                  fill="#ffffff"
                                  textAnchor="middle"
                                >
                                  {Math.round(val)}%
                                </text>
                              ) : null}
                              {!percentStack && showLabels && rw > 24 ? (
                                <text
                                  x={cx}
                                  y={cy + 3}
                                  fontSize={labelFontSize}
                                  fill={labelColor}
                                  textAnchor="middle"
                                  stroke={labelStroke}
                                  strokeWidth={labelStrokeWidth}
                                >
                                  {String(raw)}
                                </text>
                              ) : null}
                            </g>
                          );
                        });
                      });
                    })}
                  </g>
                );
              }
            }
            return null;
          })()}
        </svg>
      ) : (
        <div className="h-full w-full flex justify-center items-center text-gray-400">Nothing is Selected</div>
      )}
      {isDiscrete && scrollEnabled && (
        <div
          style={{
            position: "absolute",
            left: padding.left,
            top: padding.top,
            width: innerW,
            height: innerH + 28,
            overflowX: "auto",
            overflowY: "hidden",
          }}
          ref={scrollXRef}
          onScroll={(e) => {
            const sl = e.currentTarget.scrollLeft;
            const bandX = contentInnerW / (xCount || 1);
            const start = Math.max(0, Math.floor(sl / bandX));
            const visCount = Math.max(
              1,
              Math.min(xCount - start, Math.ceil(innerW / bandX))
            );
            setXScrollInfo({ start, count: visCount });
            if (!(type === "column" && axisY?.rescaleOnScroll)) return;
            const end = Math.min(xCount - 1, Math.floor((sl + innerW) / bandX));
            let vMin = Infinity;
            let vMax = -Infinity;
            if (stacked && !percentStack) {
              for (let i = start; i <= end; i++) {
                const tot = totalsPerCategory[i] || 0;
                if (tot < vMin) vMin = tot;
                if (tot > vMax) vMax = tot;
              }
            } else {
              displayEffective.forEach((en) => {
                en.points.forEach((p, i) => {
                  if (i >= start && i <= end) {
                    if (typeof p.y === "number") {
                      if (p.y < vMin) vMin = p.y;
                      if (p.y > vMax) vMax = p.y;
                    }
                  }
                });
              });
            }
            if (vMin === Infinity) {
              vMin = minY;
              vMax = maxY;
            }
            const gridLinesOpt =
              axisY?.gridLines ?? config.axis?.y?.gridLines ?? 5;
            const clampMinZero =
              axisY?.clampMinZero ?? config.axis?.y?.clampMinZero ?? false;
            const baseMin = clampMinZero && vMin > 0 ? 0 : Math.floor(vMin);
            const range = Math.max(1, vMax - baseMin);
            const stepOverride = axisY?.step;
            const niceNumber = (value) => {
              const exp = Math.floor(Math.log10(value));
              const f = value / Math.pow(10, exp);
              let nf = 1;
              if (f <= 1.5) nf = 1;
              else if (f <= 3) nf = 2;
              else if (f <= 7) nf = 5;
              else nf = 10;
              return nf * Math.pow(10, exp);
            };
            const step =
              typeof stepOverride === "number" && stepOverride > 0
                ? stepOverride
                : niceNumber(range / gridLinesOpt);
            const niceMin = Math.floor(baseMin / step) * step;
            const segments = Math.max(1, Math.ceil((vMax - niceMin) / step));
            const maxUse = niceMin + segments * step;
            const ticks = Array.from({ length: segments + 1 }, (_, ti) => {
              const val = niceMin + ti * step;
              const py = padding.top + innerH - (ti * innerH) / segments;
              const showInt = step >= 1;
              return {
                value: showInt ? Math.round(val) : Math.round(val * 100) / 100,
                y: py,
              };
            });
            setYScrollScale({ minY: niceMin, maxY: maxUse, yTicks: ticks });
          }}
        >
          {(() => {
            const bandX = contentInnerW / (xCount || 1);
            const useMinY =
              type === "column" && axisY?.rescaleOnScroll && yScrollScale
                ? yScrollScale.minY
                : minY;
            const useMaxY =
              type === "column" && axisY?.rescaleOnScroll && yScrollScale
                ? yScrollScale.maxY
                : maxY;
            const yContent = (v) =>
              innerH - ((v - useMinY) / (useMaxY - useMinY || 1)) * innerH;
            const clampZeroScroll =
              (options.axis?.y ?? options.yAxis)?.clampMinZero ??
              config.axis?.y?.clampMinZero ??
              false;
            const baselineValScroll = clampZeroScroll
              ? 0
              : useMinY > 0
              ? useMinY
              : useMaxY < 0
              ? useMaxY
              : 0;
            const baseYContent = yContent(baselineValScroll);
            const gap = options.bar?.gap ?? 6;
            const radius = options.bar?.radius ?? 2;
            const seriesN = Math.max(1, displayEffective.length);
            const fixedW = options.bar?.fixedWidth;
            const groupWidth = Math.max(
              1,
              bandX - groupGapPx - gap * (seriesN + 1)
            );
            const perSeriesW =
              typeof fixedW === "number" && fixedW > 0
                ? fixedW
                : Math.max(1, groupWidth / seriesN);
            return (
              <svg width={contentInnerW} height={innerH + 24}>
                {(options.grid?.vertical ?? config.grid.vertical) &&
                  xCount > 0 &&
                  Array.from({ length: xCount }).map((_, i) => (
                    <line
                      key={`grid-v-scroll-${i}`}
                      x1={i * bandX + bandX / 2}
                      y1={0}
                      x2={i * bandX + bandX / 2}
                      y2={innerH}
                      stroke={options.grid?.color || config.grid.color}
                      strokeWidth={
                        options.grid?.strokeWidth ?? config.grid.strokeWidth
                      }
                      strokeDasharray={
                        options.grid?.dasharray ?? config.grid.dasharray
                      }
                    />
                  ))}
                {(() => {
                  if (!stacked) {
                    const columnLabelSlots = Array.from(
                      { length: xCount },
                      () => []
                    );
                    return displayEffective.map((e, si) => (
                      <g key={`bars-${si}`}>
                        {e.points.map((p, i) => {
                          const idx = typeof p.i === "number" ? p.i : i;
                          const avail = bandX - groupGapPx;
                          const groupWidth =
                            perSeriesW * seriesN +
                            gap * Math.max(0, seriesN - 1);
                          const groupStart =
                            idx * bandX +
                            groupGapPx / 2 +
                            (avail - groupWidth) / 2;
                          const gx = groupStart + si * (perSeriesW + gap);
                          const vy = yContent(p.y);
                          const ry = Math.min(vy, baseYContent);
                          const rh = Math.abs(baseYContent - vy);
                          return (
                            <g key={`c-${si}-${idx}`}>
                              <rect
                                x={gx}
                                y={ry}
                                width={perSeriesW}
                                height={rh}
                                fill={colorFor(e)}
                                rx={radius}
                                onMouseEnter={() => {
                                  const estWidth = 160;
                                  const sl =
                                    scrollXRef.current?.scrollLeft || 0;
                                  const px2 = gx + perSeriesW / 2;
                                  const py2 = ry;
                                  let left = padding.left + (px2 - sl) + 10;
                                  let top = padding.top + py2 - 24;
                                  if (left + estWidth > dims.w - padding.right)
                                    left =
                                      padding.left + (px2 - sl) - estWidth - 10;
                                  if (top < padding.top)
                                    top = padding.top + py2 + 12;
                                  setTt({
                                    visible: true,
                                    x: left,
                                    y: top,
                                    title: `${
                                      e.name ?? `Series ${e.origIdx + 1}`
                                    }`,
                                    subtitle: `${
                                      effectiveCategories?.[idx] ?? idx + 1
                                    }: ${p.y}`,
                                  });
                                }}
                                onMouseLeave={() =>
                                  setTt((t) => ({ ...t, visible: false }))
                                }
                              />
                              {showLabels && rh > 14 && perSeriesW >= 10
                                ? (() => {
                                    const totalCat =
                                      totalsPerCategory[idx] || 0;
                                    const pct =
                                      totalCat > 0
                                        ? Math.round(
                                            ((p.y || 0) / totalCat) * 100
                                          )
                                        : 0;
                                    const text =
                                      labelValueDisplay === "percentage"
                                        ? `${pct}%`
                                        : labelValueDisplay ===
                                          "value_percentage"
                                        ? `${String(p.y)} / ${pct}%`
                                        : String(p.y);
                                    const baseY = ry - 4;
                                    const slots = columnLabelSlots[idx];
                                    let offsetN = 0;
                                    for (let k = 0; k < slots.length; k++) {
                                      if (
                                        Math.abs(
                                          slots[k] -
                                            (baseY -
                                              offsetN * (labelFontSize + 2))
                                        ) < (labelCfg.minGap ?? 12)
                                      ) {
                                        offsetN++;
                                      }
                                    }
                                    const pyFinal =
                                      baseY - offsetN * (labelFontSize + 2);
                                    slots.push(pyFinal);
                                    return (
                                      <text
                                        x={gx + perSeriesW / 2}
                                        y={pyFinal}
                                        fontSize={labelFontSize}
                                        textAnchor="middle"
                                        fill={labelColor}
                                      >
                                        {text}
                                      </text>
                                    );
                                  })()
                                : null}
                            </g>
                          );
                        })}
                      </g>
                    ));
                  }
                  const seriesToDraw = percentStack
                    ? normalizedEffective
                    : displayEffective;
                  const fixedW = options.bar?.fixedWidth;
                  const avail = Math.max(1, bandX - groupGapPx);
                  const barFullW =
                    typeof fixedW === "number" && fixedW > 0
                      ? Math.min(fixedW, avail)
                      : Math.max(1, avail - gap * 2);
                  const groupedMode =
                    stacked &&
                    (options.bar?.grouped === true ||
                      options.chart?.grouped === true);
                  if (!groupedMode) {
                    return (
                      <g key={`stack-col-scroll`}>
                        {Array.from({ length: xCount }).map((_, i) => {
                          const gx =
                            i * bandX + groupGapPx / 2 + (avail - barFullW) / 2;
                          let acc = 0;
                          return seriesToDraw.map((e, si) => {
                            const val = e.points[i]?.y || 0;
                            const y1 = yContent(acc);
                            const nextAcc =
                              percentStack && si === seriesToDraw.length - 1
                                ? 100
                                : acc + val;
                            const y2 = yContent(nextAcc);
                            const ry = Math.min(y1, y2);
                            const rh = Math.abs(y1 - y2);
                            const c = colorFor(e);
                            acc = nextAcc;
                            const px = gx + barFullW / 2;
                            const py = ry + rh / 2;
                            const rawVal = percentStack
                              ? displayEffective[si]?.points[i]?.y ?? 0
                              : val;
                            return (
                              <g key={`scs-${si}-${i}`}>
                                <rect
                                  x={gx}
                                  y={ry}
                                  width={barFullW}
                                  height={rh}
                                  fill={c}
                                  rx={radius}
                                  onMouseEnter={() => {
                                    const estWidth = 160;
                                    const sl =
                                      scrollXRef.current?.scrollLeft || 0;
                                    let left = padding.left + (px - sl) + 10;
                                    let top = padding.top + py - 24;
                                    if (
                                      left + estWidth >
                                      dims.w - padding.right
                                    )
                                      left =
                                        padding.left +
                                        (px - sl) -
                                        estWidth -
                                        10;
                                    if (top < padding.top)
                                      top = padding.top + py + 12;
                                    const pctText = percentStack
                                      ? `${Math.round(val)}%`
                                      : `${rawVal}`;
                                    setTt({
                                      visible: true,
                                      x: left,
                                      y: top,
                                      title: `${
                                        e.name ?? `Series ${e.origIdx + 1}`
                                      }`,
                                      subtitle: `${
                                        effectiveCategories?.[i] ?? i + 1
                                      }: ${pctText}${
                                        percentStack ? ` (${rawVal})` : ""
                                      }`,
                                    });
                                  }}
                                  onMouseLeave={() =>
                                    setTt((t) => ({ ...t, visible: false }))
                                  }
                                />
                                {percentStack && rh > 12 ? (
                                  <text
                                    x={px}
                                    y={py + 3}
                                    fontSize={10}
                                    fill="#ffffff"
                                    textAnchor="middle"
                                  >
                                    {Math.round(val)}%
                                  </text>
                                ) : null}
                                {!percentStack && showLabels && rh > 12
                                  ? (() => {
                                      const totalCat =
                                        totalsPerCategory[i] || 0;
                                      const rawForCat = rawVal;
                                      const pct =
                                        totalCat > 0
                                          ? Math.round(
                                              ((rawForCat || 0) / totalCat) *
                                                100
                                            )
                                          : 0;
                                      const text =
                                        labelValueDisplay === "percentage"
                                          ? `${pct}%`
                                          : labelValueDisplay ===
                                            "value_percentage"
                                          ? `${String(rawForCat)} / ${pct}%`
                                          : String(rawForCat);
                                      return (
                                        <text
                                          x={px}
                                          y={py + 3}
                                          fontSize={labelFontSize}
                                          fill={labelColor}
                                          textAnchor="middle"
                                          stroke={labelStroke}
                                          strokeWidth={labelStrokeWidth}
                                        >
                                          {text}
                                        </text>
                                      );
                                    })()
                                  : null}
                              </g>
                            );
                          });
                        })}
                      </g>
                    );
                  }
                  const groupKeys = Array.from(
                    new Set(effective.map((e) => e.group ?? ""))
                  );
                  const groupCount = Math.max(1, groupKeys.length);
                  const groupGap = options.bar?.groupGap ?? 0;
                  const perGroupW =
                    typeof fixedW === "number" && fixedW > 0
                      ? Math.min(fixedW, avail)
                      : Math.max(
                          1,
                          Math.floor(
                            (Math.max(1, avail - gap * 2) -
                              groupGap * Math.max(0, groupCount - 1)) /
                              groupCount
                          )
                        );
                  const barTotalW = Math.min(
                    avail,
                    perGroupW * groupCount +
                      groupGap * Math.max(0, groupCount - 1)
                  );
                  return (
                    <g key={`gstack-col-scroll`}>
                      {Array.from({ length: xCount }).map((_, i) => {
                        const bandStart =
                          i * bandX + groupGapPx / 2 + (avail - barTotalW) / 2;
                        return groupKeys.map((gk, gi) => {
                          const gx = bandStart + gi * (perGroupW + groupGap);
                          let acc = 0;
                          const groupSeries = seriesToDraw.filter(
                            (e) => (e.group ?? "") === gk
                          );
                          const groupTotalRaw = groupSeries.reduce(
                            (sum, e) =>
                              sum + (effective[e.origIdx]?.points[i]?.y || 0),
                            0
                          );
                          return groupSeries.map((e, si) => {
                            const raw = effective[e.origIdx]?.points[i]?.y || 0;
                            const val = percentStack
                              ? groupTotalRaw
                                ? (raw / groupTotalRaw) * 100
                                : 0
                              : raw;
                            const y1 = yContent(acc);
                            const nextAcc = acc + val;
                            const y2 = yContent(nextAcc);
                            const ry = Math.min(y1, y2);
                            const rh = Math.abs(y1 - y2);
                            const c = colorFor(e);
                            acc = nextAcc;
                            const px = gx + perGroupW / 2;
                            const py = ry + rh / 2;
                            const pctText = percentStack
                              ? `${Math.round(val)}%`
                              : `${raw}`;
                            return (
                              <g key={`gscs-${gi}-${si}-${i}`}>
                                <rect
                                  x={gx}
                                  y={ry}
                                  width={perGroupW}
                                  height={rh}
                                  fill={c}
                                  rx={radius}
                                  onMouseEnter={() => {
                                    const estWidth = 160;
                                    const sl =
                                      scrollXRef.current?.scrollLeft || 0;
                                    let left = padding.left + (px - sl) + 10;
                                    let top = padding.top + py - 24;
                                    if (
                                      left + estWidth >
                                      dims.w - padding.right
                                    )
                                      left =
                                        padding.left +
                                        (px - sl) -
                                        estWidth -
                                        10;
                                    if (top < padding.top)
                                      top = padding.top + py + 12;
                                    const monthLabel =
                                      effectiveCategories?.[i] ?? i + 1;
                                    const infoList = groupSeries
                                      .map((ge) => {
                                        const rawVal =
                                          effective[ge.origIdx]?.points[i]?.y ||
                                          0;
                                        return `${ge.name}: ${rawVal}`;
                                      })
                                      .join("\n");
                                    setTt({
                                      visible: true,
                                      x: left,
                                      y: top,
                                      title: `${gk} — ${monthLabel}`,
                                      subtitle: infoList,
                                    });
                                  }}
                                  onMouseLeave={() =>
                                    setTt((t) => ({ ...t, visible: false }))
                                  }
                                />
                                {percentStack && rh > 12 ? (
                                  <text
                                    x={px}
                                    y={py + 3}
                                    fontSize={10}
                                    fill="#ffffff"
                                    textAnchor="middle"
                                  >
                                    {Math.round(val)}%
                                  </text>
                                ) : null}
                                {!percentStack && showLabels && rh > 12 ? (
                                  <text
                                    x={px}
                                    y={py + 3}
                                    fontSize={labelFontSize}
                                    fill={labelColor}
                                    textAnchor="middle"
                                    stroke={labelStroke}
                                    strokeWidth={labelStrokeWidth}
                                  >
                                    {String(raw)}
                                  </text>
                                ) : null}
                              </g>
                            );
                          });
                        });
                      })}
                    </g>
                  );
                })()}
                {(() => {
                  const bandX = contentInnerW / (xCount || 1);
                  return Array.from({ length: xCount }).map((_, i) => {
                    const lx = i * bandX + bandX / 2;
                    return (
                      <text
                        key={`xlab-scroll-${i}`}
                        x={lx}
                        y={innerH + 14}
                        fontSize={10}
                        fill={
                          options.axis?.labelColor || config.axis.labelColor
                        }
                        textAnchor="middle"
                      >
                        {effectiveCategories?.[i] ?? i + 1}
                      </text>
                    );
                  });
                })()}
              </svg>
            );
          })()}
        </div>
      )}
      {(type === "line" || type === "area") && lineScrollEnabled && (
        <div
          style={{
            position: "absolute",
            left: padding.left,
            top: padding.top,
            width: innerW,
            height: innerH + 28,
            overflowX: "auto",
            overflowY: "hidden",
          }}
          ref={scrollXRef}
        >
          {(() => {
            const bandX = xCount > 1 ? desiredLineBandWidth : 0;
            const yContent = (v) =>
              innerH - ((v - minY) / (maxY - minY || 1)) * innerH;
            const buildPathContent = (data) => {
              if (!Array.isArray(data) || data.length === 0) return "";
              const strokeSrc = isLineLike
                ? options.line?.stroke ?? options.stroke
                : options.stroke;
              const curve = strokeSrc?.curve ?? "straight";
              if (curve === "smooth") {
                const pts = data.map((v, i) => ({
                  x: edgePadLine + i * bandX,
                  y: yContent(v),
                }));
                if (pts.length < 2) return `M ${pts[0].x} ${pts[0].y}`;
                const t = Math.max(0, Math.min(1, strokeSrc?.tension ?? 0.4));
                let d = `M ${pts[0].x} ${pts[0].y}`;
                for (let i = 0; i < pts.length - 1; i++) {
                  const p0 = i > 0 ? pts[i - 1] : pts[i];
                  const p1 = pts[i];
                  const p2 = pts[i + 1];
                  const p3 = i + 2 < pts.length ? pts[i + 2] : p2;
                  const c1x = p1.x + ((p2.x - p0.x) / 6) * t;
                  const c1y = p1.y + ((p2.y - p0.y) / 6) * t;
                  const c2x = p2.x - ((p3.x - p1.x) / 6) * t;
                  const c2y = p2.y - ((p3.y - p1.y) / 6) * t;
                  d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${p2.x} ${p2.y}`;
                }
                return d;
              } else if (curve === "stepline") {
                let d = `M ${edgePadLine} ${yContent(data[0])}`;
                for (let i = 1; i < data.length; i++) {
                  const prevY = yContent(data[i - 1]);
                  const currY = yContent(data[i]);
                  const xi = edgePadLine + i * bandX;
                  d += ` L ${xi} ${prevY} L ${xi} ${currY}`;
                }
                return d;
              }
              const pts = data.map(
                (v, i) =>
                  `${i === 0 ? "M" : "L"} ${edgePadLine + i * bandX} ${yContent(
                    v
                  )}`
              );
              return pts.join(" ");
            };
            const lineLabelSlots = Array.from({ length: xCount }, () => []);
            return (
              <svg width={contentInnerWLine} height={innerH + 24}>
                {(options.grid?.vertical ?? config.grid.vertical) &&
                  xCount > 0 &&
                  Array.from({ length: xCount }).map((_, i) => (
                    <line
                      key={`grid-v-line-scroll-${i}`}
                      x1={edgePadLine + i * bandX}
                      y1={0}
                      x2={edgePadLine + i * bandX}
                      y2={innerH}
                      stroke={options.grid?.color || config.grid.color}
                      strokeWidth={
                        options.grid?.strokeWidth ?? config.grid.strokeWidth
                      }
                      strokeDasharray={
                        options.grid?.dasharray ?? config.grid.dasharray
                      }
                    />
                  ))}
                {displayEffective.map((e, si) => {
                  const color = colorFor(e);
                  if (hidden.has(si)) return null;
                  const data = e.points.map((p) => p.y);
                  const pathD = buildPathContent(data);
                  return (
                    <g key={`s-line-scroll-${si}`}>
                      <path
                        d={pathD}
                        fill="none"
                        stroke={color}
                        strokeWidth={strokeCfg?.width ?? 2}
                        strokeDasharray={
                          strokeCfg?.dashArray ??
                          strokeCfg?.dasharray ??
                          undefined
                        }
                      />
                      {(() => {
                        const fillType = fillCfg?.type ?? "none";
                        if (type !== "area" || !fillType || fillType === "none")
                          return null;
                        if (!Array.isArray(data) || data.length === 0)
                          return null;
                        let d = pathD;
                        const lastX = edgePadLine + (data.length - 1) * bandX;
                        const baseY = innerH;
                        d += ` L ${lastX} ${baseY} L ${edgePadLine} ${baseY} Z`;
                        if (fillType === "solid") {
                          const alpha = fillCfg?.opacity ?? 0.15;
                          return <path d={d} fill={hexToRgba(color, alpha)} />;
                        }
                        if (fillType === "gradient") {
                          const gid = `grad-line-scroll-${si}`;
                          const alpha = fillCfg?.opacity ?? 0.15;
                          return (
                            <g>
                              <defs>
                                <linearGradient
                                  id={gid}
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop
                                    offset="0%"
                                    stopColor={hexToRgba(color, alpha)}
                                  />
                                  <stop
                                    offset="100%"
                                    stopColor={hexToRgba(color, 0)}
                                  />
                                </linearGradient>
                              </defs>
                              <path d={d} fill={`url(#${gid})`} />
                            </g>
                          );
                        }
                        return null;
                      })()}
                      {e.points.map((p, i) => (
                        <circle
                          key={`p-line-scroll-${si}-${i}`}
                          cx={edgePadLine + i * bandX}
                          cy={yContent(p.y)}
                          r={markersCfg?.size ?? 2.5}
                          fill={color}
                          onMouseEnter={() => {
                            const px = edgePadLine + i * bandX;
                            const py = yContent(p.y);
                            const estWidth = 160;
                            const sl = scrollXRef.current?.scrollLeft || 0;
                            let left = padding.left + (px - sl) + 10;
                            let top = padding.top + py - 24;
                            if (left + estWidth > dims.w - padding.right)
                              left = padding.left + (px - sl) - estWidth - 10;
                            if (top < padding.top) top = padding.top + py + 12;
                            setTt({
                              visible: true,
                              x: left,
                              y: top,
                              title: `${e.name ?? `Series ${si + 1}`}`,
                              subtitle: `${
                                effectiveCategories?.[i] ?? i + 1
                              }: ${p.y}`,
                            });
                          }}
                          onMouseLeave={() =>
                            setTt((t) => ({ ...t, visible: false }))
                          }
                        />
                      ))}
                      {showLabels &&
                        e.points.map((p, i) => {
                          const totalCat = totalsPerCategory[i] || 0;
                          const pct =
                            totalCat > 0
                              ? Math.round((p.y / totalCat) * 100)
                              : 0;
                          const text =
                            labelValueDisplay === "percentage"
                              ? `${pct}%`
                              : labelValueDisplay === "value_percentage"
                              ? `${String(p.y)} / ${pct}%`
                              : String(p.y);
                          const baseY = yContent(p.y) - 8;
                          const slots = lineLabelSlots[i];
                          let pyFinal = baseY;
                          if (labelStrategy === "hide") {
                            for (let s of slots) {
                              if (Math.abs(s - baseY) < labelMinGap)
                                return null;
                            }
                          } else {
                            const step = labelFontSize + 2;
                            const candidates = [0];
                            for (let k = 1; k <= labelMaxStack; k++) {
                              candidates.push(-k * step, k * step);
                            }
                            let placed = false;
                            for (let ci = 0; ci < candidates.length; ci++) {
                              const cand = baseY + candidates[ci];
                              let collides = false;
                              for (let s of slots) {
                                if (Math.abs(s - cand) < labelMinGap) {
                                  collides = true;
                                  break;
                                }
                              }
                              if (!collides) {
                                pyFinal = cand;
                                placed = true;
                                break;
                              }
                            }
                            if (!placed && labelStrategy === "hide")
                              return null;
                          }
                          slots.push(pyFinal);
                          return (
                            <text
                              key={`pl-line-scroll-${si}-${i}`}
                              x={edgePadLine + i * bandX}
                              y={pyFinal}
                              fontSize={labelFontSize}
                              textAnchor="middle"
                              fill={labelColor}
                              stroke={labelStroke}
                              strokeWidth={labelStrokeWidth}
                            >
                              {text}
                            </text>
                          );
                        })}
                    </g>
                  );
                })}
                {(() => {
                  return Array.from({ length: xCount }).map((_, i) => {
                    const lx = edgePadLine + i * bandX;
                    const anchor =
                      i === 0 ? "start" : i === xCount - 1 ? "end" : "middle";
                    return (
                      <text
                        key={`xlab-line-scroll-${i}`}
                        x={lx}
                        y={innerH + 14}
                        fontSize={10}
                        fill={
                          options.axis?.labelColor || config.axis.labelColor
                        }
                        textAnchor={anchor}
                      >
                        {effectiveCategories?.[i] ?? i + 1}
                      </text>
                    );
                  });
                })()}
              </svg>
            );
          })()}
        </div>
      )}
      {options.legend?.show !== false && legendMode === "select" ? (
        series.length > 0 ? (
          <div
            style={{
              position: "absolute",
              top: 4,
              right: 6,
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              flexWrap: "wrap",
              fontSize: 12,
              overflow: "auto",
              maxWidth: options.legend?.maxWidth ?? 260,
              zIndex: 40,
            }}
          >
            {(() => {
              const isGrouped =
                (options.bar?.grouped === true ||
                  options.chart?.grouped === true) &&
                (options.bar?.stacked === true ||
                  options.chart?.stacked === true);
              if (isGrouped) {
                const names = Array.from(new Set(series.map((s) => s.name)));
                return (
                  <Select
                    options={names.map((n) => ({ label: n, value: n }))}
                    value={selectedNames}
                    onChange={(vals) => setSelectedNames(vals)}
                    placeholder="Legend"
                    maxHeight={140}
                    colors={Object.fromEntries(
                      names.map((n) => [n, colorMap[n] || colors[0]])
                    )}
                  />
                );
              }
              return (
                <Select
                  options={series.map((s, idx) => ({
                    label: s.name ?? `Series ${idx + 1}`,
                    value: idx,
                  }))}
                  value={selectedIdx}
                  onChange={(vals) => setSelectedIdx(vals)}
                  placeholder="Legend"
                  maxHeight={140}
                  colors={Object.fromEntries(
                    series.map((_, idx) => [idx, colors[idx % colors.length]])
                  )}
                />
              );
            })()}
          </div>
        ) : null
      ) : null}

      {type === "bar" && scrollBarEnabled && (
        <div
          style={{
            position: "absolute",
            left: padding.left - yLabelGutter,
            top: padding.top,
            width: innerW + yLabelGutter + scrollbarComp,
            height: innerH,
            overflowY: "auto",
            overflowX: "hidden",
          }}
          ref={scrollYRef}
        >
          <div style={{ display: "flex", width: innerW + yLabelGutter }}>
            {(() => {
              const bandY = contentInnerH / (xCount || 1);
              return (
                <svg width={yLabelGutter} height={contentInnerH}>
                  {Array.from({ length: xCount }).map((_, i) => (
                    <text
                      key={`ylab-cat-scroll-${i}`}
                      x={yLabelGutter - 8}
                      y={i * bandY + bandY / 2 + 3}
                      fontSize={10}
                      fill={options.axis?.labelColor || config.axis.labelColor}
                      textAnchor="end"
                    >
                      {effectiveCategories?.[i] ?? i + 1}
                    </text>
                  ))}
                </svg>
              );
            })()}
            {(() => {
              const bandY = contentInnerH / (xCount || 1);
              const valX = (v) => ((v - minY) / (maxY - minY || 1)) * innerW;
              const baselineValY = minY > 0 ? minY : maxY < 0 ? maxY : 0;
              const baseXContent = valX(baselineValY);
              const gap = options.bar?.gap ?? 6;
              const radius = options.bar?.radius ?? 2;
              const seriesN = Math.max(1, displayEffective.length);
              const fixedH = options.bar?.fixedHeight;
              const groupHeight = Math.max(
                1,
                bandY - groupGapPx - gap * (seriesN + 1)
              );
              const perSeriesH =
                typeof fixedH === "number" && fixedH > 0
                  ? fixedH
                  : Math.max(1, groupHeight / seriesN);
              return (
                <svg width={innerW} height={contentInnerH}>
                  {(() => {
                    if (!stacked) {
                      return displayEffective.map((e, si) => (
                        <g key={`bars-scrollY-${si}`}>
                          {e.points.map((p, i) => {
                            const gy =
                              i * bandY +
                              groupGapPx / 2 +
                              gap +
                              si * (perSeriesH + gap);
                            const vx = valX(p.y);
                            const rx2 = Math.min(vx, baseXContent);
                            const rw = Math.abs(vx - baseXContent);
                            return (
                              <g key={`b-scrollY-${si}-${i}`}>
                                <rect
                                  x={rx2}
                                  y={gy}
                                  width={rw}
                                  height={perSeriesH}
                                  fill={colorFor(e)}
                                  ry={radius}
                                  onMouseEnter={() => {
                                    const estWidth = 160;
                                    const cx = rx2 + rw / 2;
                                    const cy = gy + perSeriesH / 2;
                                    const st =
                                      scrollYRef.current?.scrollTop || 0;
                                    let left =
                                      padding.left - yLabelGutter + cx + 10;
                                    let top = padding.top + (cy - st) - 24;
                                    if (
                                      left + estWidth >
                                      dims.w - padding.right
                                    )
                                      left =
                                        padding.left -
                                        yLabelGutter +
                                        cx -
                                        estWidth -
                                        10;
                                    if (top < padding.top)
                                      top = padding.top + (cy - st) + 12;
                                    setTt({
                                      visible: true,
                                      x: left,
                                      y: top,
                                      title: `${e.name ?? `Series ${si + 1}`}`,
                                      subtitle: `${
                                        effectiveCategories?.[i] ?? i + 1
                                      }: ${p.y}`,
                                    });
                                  }}
                                  onMouseLeave={() =>
                                    setTt((t) => ({ ...t, visible: false }))
                                  }
                                />
                                {showLabels && rw > 20 && perSeriesH >= 10
                                  ? (() => {
                                      const totalCat =
                                        totalsPerCategory[i] || 0;
                                      const pct =
                                        totalCat > 0
                                          ? Math.round(
                                              ((p.y || 0) / totalCat) * 100
                                            )
                                          : 0;
                                      const text =
                                        labelValueDisplay === "percentage"
                                          ? `${pct}%`
                                          : labelValueDisplay ===
                                            "value_percentage"
                                          ? `${String(p.y)} / ${pct}%`
                                          : String(p.y);
                                      return (
                                        <text
                                          x={rx2 + rw + 4}
                                          y={gy + perSeriesH / 2 + 3}
                                          fontSize={labelFontSize}
                                          textAnchor="start"
                                          fill={labelColor}
                                        >
                                          {text}
                                        </text>
                                      );
                                    })()
                                  : null}
                              </g>
                            );
                          })}
                        </g>
                      ));
                    }
                    const fixedH = options.bar?.fixedHeight;
                    const avail = Math.max(1, bandY - groupGapPx);
                    const barFullH =
                      typeof fixedH === "number" && fixedH > 0
                        ? Math.min(fixedH, avail)
                        : Math.max(1, avail - gap * 2);
                    const seriesToDraw = percentStack
                      ? normalizedEffective
                      : displayEffective;
                    const groupedMode =
                      stacked &&
                      (options.bar?.grouped === true ||
                        options.chart?.grouped === true);
                    if (!groupedMode) {
                      return (
                        <g key={`stack-bar-scroll`}>
                          {Array.from({ length: xCount }).map((_, i) => {
                            const gy =
                              i * bandY +
                              groupGapPx / 2 +
                              (avail - barFullH) / 2;
                            let acc = 0;
                            return seriesToDraw.map((e, si) => {
                              const val = e.points[i]?.y || 0;
                              const x1 = valX(acc);
                              const nextAcc =
                                percentStack && si === seriesToDraw.length - 1
                                  ? 100
                                  : acc + val;
                              const x2 = valX(nextAcc);
                              const rx2 = Math.min(x1, x2);
                              const rw = Math.abs(x2 - x1);
                              const c = colorFor(e);
                              acc = nextAcc;
                              const cx = rx2 + rw / 2;
                              const cy = gy + barFullH / 2;
                              const rawVal = percentStack
                                ? displayEffective[si]?.points[i]?.y ?? 0
                                : val;
                              return (
                                <g key={`sbs-${si}-${i}`}>
                                  <rect
                                    x={rx2}
                                    y={gy}
                                    width={rw}
                                    height={barFullH}
                                    fill={c}
                                    ry={radius}
                                    onMouseEnter={() => {
                                      const estWidth = 160;
                                      const st =
                                        scrollYRef.current?.scrollTop || 0;
                                      let left =
                                        padding.left - yLabelGutter + cx + 10;
                                      let top = padding.top + (cy - st) - 24;
                                      if (
                                        left + estWidth >
                                        dims.w - padding.right
                                      )
                                        left =
                                          padding.left -
                                          yLabelGutter +
                                          cx -
                                          estWidth -
                                          10;
                                      if (top < padding.top)
                                        top = padding.top + (cy - st) + 12;
                                      const pctText = percentStack
                                        ? `${Math.round(val)}%`
                                        : `${rawVal}`;
                                      setTt({
                                        visible: true,
                                        x: left,
                                        y: top,
                                        title: `${
                                          e.name ?? `Series ${e.origIdx + 1}`
                                        }`,
                                        subtitle: `${
                                          effectiveCategories?.[i] ?? i + 1
                                        }: ${pctText}${
                                          percentStack ? ` (${rawVal})` : ""
                                        }`,
                                      });
                                    }}
                                    onMouseLeave={() =>
                                      setTt((t) => ({ ...t, visible: false }))
                                    }
                                  />
                                  {percentStack && rw > 24 ? (
                                    <text
                                      x={cx}
                                      y={cy + 3}
                                      fontSize={10}
                                      fill="#ffffff"
                                      textAnchor="middle"
                                    >
                                      {Math.round(val)}%
                                    </text>
                                  ) : null}
                                  {!percentStack && showLabels && rw > 24
                                    ? (() => {
                                        const totalCat =
                                          totalsPerCategory[i] || 0;
                                        const pct =
                                          totalCat > 0
                                            ? Math.round(
                                                ((rawVal || 0) / totalCat) * 100
                                              )
                                            : 0;
                                        const text =
                                          labelValueDisplay === "percentage"
                                            ? `${pct}%`
                                            : labelValueDisplay ===
                                              "value_percentage"
                                            ? `${String(rawVal)} / ${pct}%`
                                            : String(rawVal);
                                        return (
                                          <text
                                            x={cx}
                                            y={cy + 3}
                                            fontSize={labelFontSize}
                                            fill={labelColor}
                                            textAnchor="middle"
                                            stroke={labelStroke}
                                            strokeWidth={labelStrokeWidth}
                                          >
                                            {text}
                                          </text>
                                        );
                                      })()
                                    : null}
                                </g>
                              );
                            });
                          })}
                        </g>
                      );
                    }
                    const groupKeys = Array.from(
                      new Set(effective.map((e) => e.group ?? ""))
                    );
                    const groupCount = Math.max(1, groupKeys.length);
                    const groupGap = options.bar?.groupGap ?? 0;
                    const totalAvail = Math.max(
                      1,
                      barFullH - groupGap * Math.max(0, groupCount - 1)
                    );
                    const perGroupH = Math.max(
                      1,
                      Math.floor(totalAvail / groupCount)
                    );
                    return (
                      <g key={`gstack-bar-scroll`}>
                        {Array.from({ length: xCount }).map((_, i) => {
                          const bandStartY =
                            i * bandY + groupGapPx / 2 + (avail - barFullH) / 2;
                          return groupKeys.map((gk, gi) => {
                            const gy = bandStartY + gi * (perGroupH + groupGap);
                            let acc = 0;
                            const groupSeries = seriesToDraw.filter(
                              (e) => (e.group ?? "") === gk
                            );
                            const groupTotalRaw = groupSeries.reduce(
                              (sum, e) =>
                                sum + (effective[e.origIdx]?.points[i]?.y || 0),
                              0
                            );
                            return groupSeries.map((e, si) => {
                              const raw =
                                effective[e.origIdx]?.points[i]?.y || 0;
                              const val = percentStack
                                ? groupTotalRaw
                                  ? (raw / groupTotalRaw) * 100
                                  : 0
                                : raw;
                              const x1 = valX(acc);
                              const nextAcc = acc + val;
                              const x2 = valX(nextAcc);
                              const rx2 = Math.min(x1, x2);
                              const rw = Math.abs(x2 - x1);
                              const c = colorFor(e);
                              acc = nextAcc;
                              const cx = rx2 + rw / 2;
                              const cy = gy + perGroupH / 2;
                              const pctText = percentStack
                                ? `${Math.round(val)}%`
                                : `${raw}`;
                              return (
                                <g key={`gsbs-${gi}-${si}-${i}`}>
                                  <rect
                                    x={rx2}
                                    y={gy}
                                    width={rw}
                                    height={perGroupH}
                                    fill={c}
                                    ry={radius}
                                    onMouseEnter={() => {
                                      const estWidth = 160;
                                      const st =
                                        scrollYRef.current?.scrollTop || 0;
                                      let left =
                                        padding.left - yLabelGutter + cx + 10;
                                      let top = padding.top + (cy - st) - 24;
                                      if (
                                        left + estWidth >
                                        dims.w - padding.right
                                      )
                                        left =
                                          padding.left -
                                          yLabelGutter +
                                          cx -
                                          estWidth -
                                          10;
                                      if (top < padding.top)
                                        top = padding.top + (cy - st) + 12;
                                      const monthLabel =
                                        effectiveCategories?.[i] ?? i + 1;
                                      const infoList = groupSeries
                                        .map((ge) => {
                                          const rawVal =
                                            effective[ge.origIdx]?.points[i]
                                              ?.y || 0;
                                          return `${ge.name}: ${rawVal}`;
                                        })
                                        .join("\n");
                                      setTt({
                                        visible: true,
                                        x: left,
                                        y: top,
                                        title: `${gk} — ${monthLabel}`,
                                        subtitle: infoList,
                                      });
                                    }}
                                    onMouseLeave={() =>
                                      setTt((t) => ({ ...t, visible: false }))
                                    }
                                  />
                                  {percentStack && rw > 24 ? (
                                    <text
                                      x={cx}
                                      y={cy + 3}
                                      fontSize={10}
                                      fill="#ffffff"
                                      textAnchor="middle"
                                    >
                                      {Math.round(val)}%
                                    </text>
                                  ) : null}
                                  {!percentStack && showLabels && rw > 24 ? (
                                    <text
                                      x={cx}
                                      y={cy + 3}
                                      fontSize={labelFontSize}
                                      fill={labelColor}
                                      textAnchor="middle"
                                      stroke={labelStroke}
                                      strokeWidth={labelStrokeWidth}
                                    >
                                      {String(raw)}
                                    </text>
                                  ) : null}
                                </g>
                              );
                            });
                          });
                        })}
                      </g>
                    );
                  })()}
                </svg>
              );
            })()}
          </div>
        </div>
      )}
      <Tooltip
        visible={tt.visible}
        x={tt.x}
        y={tt.y}
        title={tt.title}
        subtitle={tt.subtitle}
      />
    </div>
  );
};

export default ChartBase;
